"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const ANIMATION_MS = 250;

// Shared by every BaseModal on the page, because modals stack: a pricing card
// opens the sign-in modal over the pricing modal, the upgrade dialog hands
// over to the success modal. When each one saved and restored body overflow
// on its own, closing them out of order restored "hidden" and left the page
// unscrollable. The lock is now counted, and Escape closes only the top one.
let scrollLocks = 0;
let savedOverflow = "";
const openStack: number[] = [];
let lastModalId = 0;

export type BaseModalProps = {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  /** max-width for the panel, e.g. "420px" or "max-w-[420px]" */
  maxWidth?: string;
  /** optional class for the panel wrapper */
  panelClassName?: string;
  /** allow closing by clicking backdrop (default true) */
  closeOnBackdrop?: boolean;
  /** size the panel to its content width instead of filling maxWidth */
  fitContent?: boolean;
  /** id of the element that names this dialog (usually its heading) */
  labelledBy?: string;
  /** accessible name for a dialog with no visible heading */
  label?: string;
};

export function BaseModal({
  isOpen,
  onClose,
  children,
  maxWidth = "max-w-[420px]",
  panelClassName = "",
  closeOnBackdrop = true,
  fitContent = false,
  labelledBy,
  label,
}: BaseModalProps) {
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }

    if (isOpen) {
      requestAnimationFrame(() => {
        setMounted(true);
        requestAnimationFrame(() => setVisible(true));
      });
    } else {
      queueMicrotask(() => setVisible(false));
      closeTimerRef.current = setTimeout(() => {
        setMounted(false);
        closeTimerRef.current = null;
      }, ANIMATION_MS);
    }

    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
    };
  }, [isOpen]);

  // Latest onClose without re-running the stack effect when a parent passes
  // a fresh callback — re-running would move this modal to the top.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const id = ++lastModalId;
    openStack.push(id);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && openStack[openStack.length - 1] === id) onCloseRef.current();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      const at = openStack.indexOf(id);
      if (at !== -1) openStack.splice(at, 1);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!mounted) return;
    if (scrollLocks++ === 0) {
      savedOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    }
    return () => {
      if (--scrollLocks === 0) document.body.style.overflow = savedOverflow;
    };
  }, [mounted]);

  useEffect(() => {
    if (isOpen) {
      openerRef.current = document.activeElement as HTMLElement | null;
      panelRef.current?.focus();
      return;
    }
    // Closing unmounts the panel under the caret, which leaves focus on
    // <body> — i.e. at the top of the document, several dozen Tab stops from
    // where the user was. Put it back, but ONLY if nothing else has claimed
    // it: an action that closes the dialog and focuses what it just created
    // (the deck's gallery does exactly that) must win.
    const opener = openerRef.current;
    openerRef.current = null;
    if (opener && document.activeElement === document.body && opener.isConnected) {
      opener.focus({ preventScroll: true });
    }
  }, [isOpen]);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (closeOnBackdrop && e.target === e.currentTarget) onClose();
    },
    [closeOnBackdrop, onClose],
  );

  if (!mounted || typeof window === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 transition-[background-color,backdrop-filter] ease-out"
      style={{
        transitionDuration: `${ANIMATION_MS}ms`,
        backgroundColor: visible ? "rgba(0,0,0,0.7)" : "rgba(0,0,0,0)",
        backdropFilter: visible ? "blur(4px)" : "blur(0px)",
        WebkitBackdropFilter: visible ? "blur(4px)" : "blur(0px)",
      }}
      onClick={handleBackdropClick}
      aria-modal="true"
      role="dialog"
      aria-labelledby={labelledBy}
      aria-label={labelledBy ? undefined : label}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        className={`relative ${fitContent ? "w-fit" : "w-full"} ${maxWidth} rounded-2xl border border-[#1e3248]/60 bg-gradient-to-b from-[#111a25] to-[#0e151e] shadow-2xl shadow-black/60 p-6 sm:p-8 outline-none transition-[opacity,transform] ease-out ${panelClassName}`}
        style={{
          transitionDuration: `${ANIMATION_MS}ms`,
          opacity: visible ? 1 : 0,
          transform: visible
            ? "scale(1) translateY(0)"
            : "scale(0.95) translateY(16px)",
        }}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}
